// ─── Roamer Intelligence Engine™ Tray ────────────────────────────────────────
// Floating live view of all pipeline jobs: capture jobs (5 stages) and
// intelligence jobs (21 stages) animate together.
// Collapsed: compact pill with active count. Expanded: full job history.
// Mounted once in App.tsx so processing stays visible across pages.
// Listens for 'nomad:knowledge-tray-open' (sealed Intelligence Engine card).

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Brain, Check, ChevronDown, ChevronUp, Copy, Loader2, Sparkles, X,
} from 'lucide-react';
import type { CaptureJob, IntelligenceJob } from '@/types/knowledgeItem';
import {
  INTELLIGENCE_EVENTS, INTELLIGENCE_STAGES, KNOWLEDGE_EVENTS, PIPELINE_STAGES,
} from '@/types/knowledgeItem';
import { dismissJob, getJobsSnapshot } from '@/services/KnowledgeCapturePipeline';
import {
  dismissIntelligenceJob, getIntelligenceJobsSnapshot,
} from '@/services/vault/RoamerIntelligenceEngine';
import { UI, timeAgo } from './knowledgeUi';

export const TRAY_OPEN_EVENT = 'nomad:knowledge-tray-open';

// ─── Capture job helpers ──────────────────────────────────────────────────────

const STAGE_INDEX: Record<string, number> = Object.fromEntries(
  PIPELINE_STAGES.map((s, i) => [s.id, i]),
);

function StageDots({ job }: { job: CaptureJob }) {
  const activeIdx = STAGE_INDEX[job.stage] ?? (
    job.stage === 'done' || job.stage === 'duplicate' ? 5 : -1
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {PIPELINE_STAGES.map((s, i) => {
        const done   = activeIdx > i || job.stage === 'done' || job.stage === 'duplicate';
        const active = activeIdx === i && job.stage !== 'error';
        const failed = job.stage === 'error' && activeIdx <= i;
        return (
          <span key={s.id} title={s.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{
              width: active ? 7 : 5, height: active ? 7 : 5, borderRadius: '50%',
              background: failed ? 'rgba(239,68,68,0.4)' : done ? UI.green : active ? UI.purple : 'rgba(255,255,255,0.15)',
              boxShadow: active ? `0 0 6px ${UI.purple}` : 'none',
              transition: 'all 200ms ease',
              animation: active ? 'ktrPulse 1.1s ease-in-out infinite' : undefined,
            }} />
            {i < PIPELINE_STAGES.length - 1 && (
              <span style={{ width: 7, height: 1, background: done ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.1)' }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

function JobRow({ job, expanded }: { job: CaptureJob; expanded: boolean }) {
  const isActive   = !['done', 'error', 'duplicate'].includes(job.stage);
  const stageLabel =
    job.stage === 'done'      ? 'Complete'  :
    job.stage === 'duplicate' ? 'Duplicate' :
    job.stage === 'error'     ? 'Failed'    :
    PIPELINE_STAGES[STAGE_INDEX[job.stage]]?.label ?? '';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.035)',
      border: `1px solid ${job.stage === 'error' ? 'rgba(239,68,68,0.3)' : UI.border}`,
      borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {isActive ? (
          <Loader2 className="ktr-spin" style={{ width: 12, height: 12, color: UI.purple, flexShrink: 0 }} />
        ) : job.stage === 'done' ? (
          <Check style={{ width: 12, height: 12, color: UI.green, flexShrink: 0 }} />
        ) : job.stage === 'duplicate' ? (
          <Copy style={{ width: 12, height: 12, color: UI.amber, flexShrink: 0 }} />
        ) : (
          <AlertTriangle style={{ width: 12, height: 12, color: UI.red, flexShrink: 0 }} />
        )}
        <p style={{
          margin: 0, flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 700, color: UI.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {job.label}
        </p>
        <span style={{ fontSize: 8.5, fontWeight: 800, color: isActive ? '#c084fc' : UI.textFaint, letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0 }}>
          {stageLabel}{isActive ? ` ${job.pct}%` : ''}
        </span>
        {!isActive && (
          <button
            onClick={() => dismissJob(job.id)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: UI.textFaint, padding: 2, display: 'flex' }}
          >
            <X style={{ width: 11, height: 11 }} />
          </button>
        )}
      </div>

      {isActive && (
        <>
          <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${job.pct}%`, borderRadius: 2,
              background: UI.gradient, transition: 'width 400ms ease',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <StageDots job={job} />
            <span style={{ fontSize: 8.5, color: UI.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {job.detail}
            </span>
          </div>
        </>
      )}

      {!isActive && (expanded || job.stage === 'error') && (
        <p style={{ margin: 0, fontSize: 9, color: job.stage === 'error' ? '#fca5a5' : UI.textDim, lineHeight: 1.45 }}>
          {job.stage === 'error' ? job.error : job.detail}
          {job.finishedAt ? ` · ${timeAgo(job.finishedAt)}` : ''}
        </p>
      )}
    </div>
  );
}

// ─── Intelligence job row ─────────────────────────────────────────────────────

function IntelligenceJobRow({ job, expanded }: { job: IntelligenceJob; expanded: boolean }) {
  const isActive  = job.status === 'running';
  const stageName = INTELLIGENCE_STAGES[job.stageIndex]?.label ?? job.stage;

  return (
    <div style={{
      background: 'rgba(139,92,246,0.04)',
      border: `1px solid ${job.status === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(139,92,246,0.18)'}`,
      borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {/* Intelligence icon — differentiates from capture jobs */}
        {isActive ? (
          <Loader2 className="ktr-spin" style={{ width: 12, height: 12, color: '#a855f7', flexShrink: 0 }} />
        ) : job.status === 'done' ? (
          <Sparkles style={{ width: 12, height: 12, color: '#4ade80', flexShrink: 0 }} />
        ) : (
          <AlertTriangle style={{ width: 12, height: 12, color: UI.red, flexShrink: 0 }} />
        )}

        {/* Brain badge */}
        <Brain style={{ width: 9, height: 9, color: 'rgba(168,85,247,0.6)', flexShrink: 0 }} />

        <p style={{
          margin: 0, flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 700, color: UI.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {job.label}
        </p>

        <span style={{ fontSize: 8.5, fontWeight: 800, color: isActive ? '#c084fc' : UI.textFaint, letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0 }}>
          {job.status === 'done' ? 'Intelligent' : job.status === 'error' ? 'Failed' : `${job.pct}%`}
        </span>

        {!isActive && (
          <button
            onClick={() => dismissIntelligenceJob(job.id)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: UI.textFaint, padding: 2, display: 'flex' }}
          >
            <X style={{ width: 11, height: 11 }} />
          </button>
        )}
      </div>

      {isActive && (
        <>
          {/* Progress bar */}
          <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${job.pct}%`, borderRadius: 2,
              background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
              transition: 'width 350ms ease',
            }} />
          </div>
          {/* Stage indicator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span style={{ fontSize: 8, color: 'rgba(168,85,247,0.7)', fontWeight: 700, letterSpacing: 0.3 }}>
              Stage {job.stageIndex + 1} of {job.totalStages}
            </span>
            <span style={{ fontSize: 8.5, color: UI.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stageName}
            </span>
          </div>
        </>
      )}

      {!isActive && (expanded || job.status === 'error') && (
        <p style={{ margin: 0, fontSize: 9, color: job.status === 'error' ? '#fca5a5' : UI.textDim, lineHeight: 1.45 }}>
          {job.status === 'error' ? job.error : job.detail}
          {job.finishedAt ? ` · ${timeAgo(job.finishedAt)}` : ''}
        </p>
      )}
    </div>
  );
}

// ─── Main tray component ──────────────────────────────────────────────────────

export default function ProcessingTray() {
  const [captureJobs,     setCaptureJobs]     = useState<CaptureJob[]>(() => getJobsSnapshot());
  const [intellJobs,      setIntellJobs]      = useState<IntelligenceJob[]>(() => getIntelligenceJobsSnapshot());
  const [expanded,        setExpanded]        = useState(false);
  const [manuallyHidden,  setManuallyHidden]  = useState(false);

  useEffect(() => {
    const onCapture = () => { setCaptureJobs(getJobsSnapshot()); setManuallyHidden(false); };
    const onIntell  = () => { setIntellJobs(getIntelligenceJobsSnapshot()); setManuallyHidden(false); };
    const onOpen    = () => {
      setCaptureJobs(getJobsSnapshot());
      setIntellJobs(getIntelligenceJobsSnapshot());
      setExpanded(true);
      setManuallyHidden(false);
    };
    window.addEventListener(KNOWLEDGE_EVENTS.jobs,  onCapture);
    window.addEventListener(INTELLIGENCE_EVENTS.jobs, onIntell);
    window.addEventListener(TRAY_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(KNOWLEDGE_EVENTS.jobs,  onCapture);
      window.removeEventListener(INTELLIGENCE_EVENTS.jobs, onIntell);
      window.removeEventListener(TRAY_OPEN_EVENT, onOpen);
    };
  }, []);

  const captureActive = useMemo(() => captureJobs.filter(j => !['done', 'error', 'duplicate'].includes(j.stage)), [captureJobs]);
  const captureErrors = useMemo(() => captureJobs.filter(j => j.stage === 'error'), [captureJobs]);
  const intellActive  = useMemo(() => intellJobs.filter(j => j.status === 'running'), [intellJobs]);
  const intellErrors  = useMemo(() => intellJobs.filter(j => j.status === 'error'), [intellJobs]);

  const totalActive = captureActive.length + intellActive.length;
  const totalErrors = captureErrors.length + intellErrors.length;

  const recentCaptureDone = useMemo(
    () => captureJobs.filter(j =>
      (j.stage === 'done' || j.stage === 'duplicate') &&
      j.finishedAt && Date.now() - new Date(j.finishedAt).getTime() < 8000,
    ),
    [captureJobs],
  );
  const recentIntellDone = useMemo(
    () => intellJobs.filter(j =>
      j.status === 'done' &&
      j.finishedAt && Date.now() - new Date(j.finishedAt).getTime() < 8000,
    ),
    [intellJobs],
  );

  // Auto-refresh so recent-done entries age out
  useEffect(() => {
    if (recentCaptureDone.length + recentIntellDone.length === 0) return;
    const t = window.setTimeout(() => {
      setCaptureJobs(getJobsSnapshot());
      setIntellJobs(getIntelligenceJobsSnapshot());
    }, 8200);
    return () => window.clearTimeout(t);
  }, [recentCaptureDone.length, recentIntellDone.length]);

  const allJobs   = captureJobs.length + intellJobs.length;
  const visibleCollapsed = [
    ...captureActive, ...captureErrors, ...recentCaptureDone,
    ...intellActive,  ...intellErrors,  ...recentIntellDone,
  ];

  if (allJobs === 0 || (!expanded && (visibleCollapsed.length === 0 || manuallyHidden))) {
    return null;
  }

  // Build display list: show capture jobs first, then intelligence jobs
  const shownCapture = expanded ? captureJobs : [...captureActive, ...captureErrors, ...recentCaptureDone].slice(0, 2);
  const shownIntell  = expanded ? intellJobs  : [...intellActive,  ...intellErrors,  ...recentIntellDone].slice(0, 2);

  return (
    <div style={{
      position: 'fixed', right: 10, bottom: 86, zIndex: 9100,
      width: 'min(320px, calc(100vw - 20px))',
      fontFamily: UI.font,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <style>{`
        @keyframes ktrPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
        .ktr-spin { animation: ktrSpin 1s linear infinite; }
        @keyframes ktrSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        background: 'rgba(5,7,15,0.96)', backdropFilter: 'blur(10px)',
        border: `1px solid ${UI.borderPurple}`, borderRadius: 13,
        boxShadow: '0 6px 30px rgba(0,0,0,0.5), 0 0 24px rgba(139,92,246,0.08)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 7,
            background: 'rgba(139,92,246,0.08)', border: 'none', cursor: 'pointer',
            padding: '8px 11px', color: UI.text,
          }}
        >
          {/* Status dot */}
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            background: totalActive > 0 ? UI.purple : totalErrors > 0 ? UI.red : UI.green,
            animation: totalActive > 0 ? 'ktrPulse 1.1s ease-in-out infinite' : undefined,
          }} />
          {/* Engine icon */}
          <Brain style={{ width: 11, height: 11, color: 'rgba(168,85,247,0.8)', flexShrink: 0 }} />
          {/* Name */}
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Roamer Intelligence Engine™
          </span>
          {/* Status badge */}
          <span style={{ fontSize: 9.5, color: UI.textDim, fontWeight: 600 }}>
            {totalActive > 0
              ? `${totalActive} active`
              : totalErrors > 0
                ? `${totalErrors} failed`
                : 'idle'}
          </span>
          <span style={{ flex: 1 }} />
          {expanded
            ? <ChevronDown style={{ width: 13, height: 13, color: UI.textDim }} />
            : <ChevronUp   style={{ width: 13, height: 13, color: UI.textDim }} />
          }
          <span
            role="button"
            tabIndex={0}
            aria-label="Hide tray"
            onClick={e => { e.stopPropagation(); setExpanded(false); setManuallyHidden(true); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); setExpanded(false); setManuallyHidden(true); } }}
            style={{ display: 'flex', color: UI.textFaint, padding: 2, cursor: 'pointer' }}
          >
            <X style={{ width: 12, height: 12 }} />
          </span>
        </button>

        {/* Job lists */}
        <div style={{
          padding: 8, display: 'flex', flexDirection: 'column', gap: 6,
          maxHeight: expanded ? 400 : 280, overflowY: 'auto',
        }}>
          {shownCapture.length === 0 && shownIntell.length === 0 ? (
            <p style={{ margin: 0, fontSize: 10, color: UI.textDim, textAlign: 'center', padding: '10px 0' }}>
              No processing activity yet — capture something!
            </p>
          ) : (
            <>
              {shownCapture.map(j => <JobRow key={j.id} job={j} expanded={expanded} />)}
              {shownIntell.map(j => <IntelligenceJobRow key={j.id} job={j} expanded={expanded} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
